import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { HeroSlideshow } from '../components/HeroSlideshow';

const IMAGES = {
  materials: 'https://customer-assets.emergentagent.com/job_a76be86f-7fd5-4574-bb32-409e5b263642/artifacts/89m8g65e_galvanized-vs-epoxy-coated-rebar-comparison-1024x585.jpg',
  infrastructure: 'https://customer-assets.emergentagent.com/job_a76be86f-7fd5-4574-bb32-409e5b263642/artifacts/0f62byna_image.png',
  digital: 'https://static.prod-images.emergentagent.com/jobs/a76be86f-7fd5-4574-bb32-409e5b263642/images/0f0fe63f43e6bc84e4dfdb9e3b7e28eb57edf6f16abfdd01de7f27a46d9c914c.png',
};

const specialityKeys = [
  'concrete_durability','service_life','infrastructure_risk','probabilistic',
  'due_diligence','low_carbon','condition','lcc','playground',
  'interior','3d','ai','waterproofing','capex',
];

export const Home = () => {
  const { t } = useTranslation();

  const studios = [
    { title: t('eng_v2.materials_title'), desc: t('eng_v2.materials_desc'), path: '/engineering/services/materials', image: IMAGES.materials },
    { title: t('eng_v2.infrastructure_title'), desc: t('eng_v2.infrastructure_desc'), path: '/engineering/services/infrastructure-risk', image: IMAGES.infrastructure },
    { title: t('eng_v2.digital_title'), desc: t('eng_v2.digital_desc'), path: '/engineering/services/digital', image: IMAGES.digital },
  ];

  return (
    <div className="min-h-screen" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>
      <SEO title={`${t('home.hero_title')} | Durable Designs`} description={t('home.hero_subtitle')} path="/" />

      <HeroSlideshow>
        <div className="max-w-3xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter leading-[1.1] mb-6" style={{fontFamily:'Plus Jakarta Sans,sans-serif'}} data-testid="hero-heading">
            {t('home.hero_title')}
          </h1>
          <p className="text-base sm:text-lg text-neutral-300 leading-relaxed mb-10 max-w-2xl">{t('home.hero_subtitle')}</p>
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <Link to="/engineering/services" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 text-sm font-medium tracking-wide transition-colors" data-testid="hero-cta">
              {t('home.hero_cta')} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/engineering/calculator/thermal" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-emerald-400/60 text-emerald-300 px-6 py-3 text-sm font-medium tracking-wide transition-colors" data-testid="hero-identity-cta">
              {t('home.hero_identity_cta')} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/engineering/calculator/carbonation" className="inline-flex items-center gap-2 border border-white/40 hover:border-white hover:bg-white/10 text-white px-6 py-3 text-sm font-medium tracking-wide transition-colors" data-testid="hero-calc-cta">
              {t('home.hero_calc_cta')}
            </Link>
          </div>
        </div>
      </HeroSlideshow>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="studios-strip">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {studios.map((s) => (
              <Link key={s.path} to={s.path} className="group border border-gray-200 hover:border-emerald-500 transition-colors overflow-hidden" data-testid={`studio-card-${s.path.split('/').pop()}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <h3 className="text-base font-bold text-gray-900 mb-2 tracking-tight" style={{fontFamily:'Plus Jakarta Sans,sans-serif'}}>{s.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed mb-3">{s.desc}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 group-hover:text-emerald-500 transition-colors">
                    {t('home.learn_more')} <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-emerald-50/30" data-testid="we-deliver-section">
        <div className="max-w-7xl mx-auto"><div className="max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 tracking-tight" style={{fontFamily:'Plus Jakarta Sans,sans-serif'}}>{t('home.we_deliver_title')}</h2>
          <p className="text-base text-gray-600 leading-relaxed mb-4">{t('home.we_deliver_p1')}</p>
          <p className="text-base text-gray-600 leading-relaxed">{t('home.we_deliver_p2')}</p>
          <p className="text-sm text-gray-500 leading-relaxed mt-6" data-testid="home-legal-entity">{t('home.we_deliver_entity')}</p>
        </div></div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="specialities-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2.5">
            {specialityKeys.map((k) => (
              <span key={k} className="px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 tracking-wide">{t(`specialities.${k}`)}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
